import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, booleanAttribute, forwardRef, inject, output } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { Editor } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import { Markdown } from '@tiptap/markdown';
import StarterKit from '@tiptap/starter-kit';
import { TiptapEditorDirective } from 'ngx-tiptap';
import { MermaidRendererDirective } from '../markdown-shared/mermaid-renderer.directive';

@Component({
  selector: 'app-tiptap-markdown-editor',
  standalone: true,
  imports: [TiptapEditorDirective, MermaidRendererDirective],
  templateUrl: './tiptap-markdown-editor.component.html',
  styleUrl: './tiptap-markdown-editor.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TiptapMarkdownEditorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TiptapMarkdownEditorComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TiptapMarkdownEditorComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private value = '';
  private onChange: (markdown: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;

  protected editor: Editor | undefined;
  protected isFullscreen = false;
  protected isDarkMode = false;
  protected isDisabled = false;
  protected isReadonly = false;
  protected isRequired = false;
  protected editorPlaceholder = '開始編輯 Markdown…';

  readonly markdownChange = output<string>();
  readonly fullscreenChange = output<boolean>();

  @Input()
  set markdown(markdown: string | null | undefined) {
    this.setMarkdown(markdown ?? '', false);
  }

  get markdown(): string {
    return this.value;
  }

  @Input()
  set placeholder(value: string) {
    this.editorPlaceholder = value || '開始編輯 Markdown…';
  }

  @Input({ transform: booleanAttribute })
  set disabled(value: boolean) {
    this.isDisabled = value;
    this.updateEditableState();
  }

  @Input({ transform: booleanAttribute })
  set readonly(value: boolean) {
    this.isReadonly = value;
    this.updateEditableState();
  }

  @Input({ transform: booleanAttribute })
  set required(value: boolean) {
    this.isRequired = value;
    this.onValidatorChange();
  }

  @Input({ transform: booleanAttribute })
  set darkMode(value: boolean) {
    this.isDarkMode = value;
  }

  @Input({ transform: booleanAttribute })
  set fullscreen(value: boolean) {
    this.isFullscreen = value;
  }

  ngOnInit(): void {
    this.editor = new Editor({
      extensions: [
        StarterKit.configure({ link: false }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          protocols: ['http', 'https', 'mailto'],
        }),
        Table.configure({ resizable: true }),
        TableRow,
        TableHeader,
        TableCell,
        TaskList,
        TaskItem.configure({ nested: true }),
        Markdown,
      ],
      editorProps: {
        attributes: {
          class: 'tiptap-markdown-editor__content',
          'aria-label': this.editorPlaceholder,
          'aria-required': String(this.isRequired),
        },
      },
      onUpdate: ({ editor }) => this.setMarkdown(editor.getMarkdown(), true),
      onBlur: () => this.markTouched(),
    });

    this.setMarkdown(this.value, false);
    this.updateEditableState();
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
  }

  writeValue(markdown: string | null): void {
    this.setMarkdown(markdown ?? '', false);
  }

  registerOnChange(fn: (markdown: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(): ValidationErrors | null {
    return this.isRequired && !this.value.trim() ? { required: true } : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  protected toggleBold(): void {
    this.editor?.chain().focus().toggleBold().run();
  }

  protected toggleItalic(): void {
    this.editor?.chain().focus().toggleItalic().run();
  }

  protected toggleStrike(): void {
    this.editor?.chain().focus().toggleStrike().run();
  }

  protected toggleHeading(level: 1 | 2 | 3): void {
    this.editor?.chain().focus().toggleHeading({ level }).run();
  }

  protected toggleBulletList(): void {
    this.editor?.chain().focus().toggleBulletList().run();
  }

  protected toggleOrderedList(): void {
    this.editor?.chain().focus().toggleOrderedList().run();
  }

  protected toggleTaskList(): void {
    this.editor?.chain().focus().toggleTaskList().run();
  }

  protected toggleBlockquote(): void {
    this.editor?.chain().focus().toggleBlockquote().run();
  }

  protected toggleCodeBlock(): void {
    this.editor?.chain().focus().toggleCodeBlock().run();
  }

  protected insertTable(): void {
    this.editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }

  protected insertLink(): void {
    const url = window.prompt('輸入連結網址');
    if (!url) {
      return;
    }

    this.editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }

  protected toggleDarkMode(): void {
    this.isDarkMode = !this.isDarkMode;
    this.cdr.markForCheck();
  }

  protected toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    this.fullscreenChange.emit(this.isFullscreen);
    this.cdr.markForCheck();
  }

  protected markTouched(): void {
    this.onTouched();
  }

  private setMarkdown(markdown: string, emit: boolean): void {
    if (markdown === this.value && (!this.editor || this.editor.getMarkdown() === markdown)) {
      return;
    }

    this.value = markdown;
    if (this.editor && this.editor.getMarkdown() !== markdown) {
      this.editor.commands.setContent(markdown, {
        contentType: 'markdown',
        emitUpdate: false,
      });
    }

    if (emit) {
      this.onChange(markdown);
      this.markdownChange.emit(markdown);
      this.onValidatorChange();
    }

    this.cdr.markForCheck();
  }

  private updateEditableState(): void {
    this.editor?.setEditable(!this.isDisabled && !this.isReadonly);
    this.cdr.markForCheck();
  }
}
